import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";

import { buildInvalidError, dayjs, type InvalidError } from "@thalamos/common";

import { CALENDAR_DAY, calendarDayOf } from "../../assessments/calendarDay.js";
import { named, type NAME } from "./named.js";

/** Facts about the wider environment a check or rule may read, as a snapshot rather than a live view. */
export type FormContext = {
  /** The moment checks are judged at, fixed when the snapshot is taken, as a UTC ISO 8601 instant. */
  readonly now: string;
};

/**
 * What is wrong, as a code rather than words: each collector words a code in
 * its own languages, so nothing that finds a problem decides how a person
 * reads about it.
 */
export type Problem = {
  readonly code: string;
  /** Anything the words for the code need, such as the date it was judged against. */
  readonly params?: Readonly<Record<string, string | number>>;
};

/** What a check found, having been able to judge the value. */
export type Verdict =
  | { readonly verdict: "passes" }
  | { readonly verdict: "fails"; readonly problem: Problem };

/**
 * A check's verdict on a value, or why it could not judge it: a value the
 * check cannot read is malformed input for that check, not a failed verdict.
 *
 * Reads time and place only from its context, so a server re-running a
 * browser's checks gets the same answer.
 */
export type Judgement<Value> = (args: {
  value: Value;
  context: FormContext;
}) => Result<Verdict, InvalidError>;

/** A judgement named by `defineCheck`, so a decorated form can say which checks it holds. */
export type Check<Value> = Judgement<Value> & { readonly [NAME]: string };

/** A check, named for whoever reads a decorated form. */
export const defineCheck = <Value>(
  name: string,
  judge: Judgement<Value>,
): Check<Value> => named(name, judge);

export const passes: Result<Verdict, InvalidError> = ok({ verdict: "passes" });

export const fails = (
  code: string,
  params?: Problem["params"],
): Result<Verdict, InvalidError> =>
  ok({
    verdict: "fails",
    problem: params === undefined ? { code } : { code, params },
  });

export const cannotJudge = (why: string): Result<Verdict, InvalidError> =>
  err(
    buildInvalidError({
      message: "The check cannot judge this value",
      issues: [why],
    }),
  );

const readDate = (value: string) => dayjs(value, CALENDAR_DAY, true);

export const isCalendarDate = defineCheck<string>(
  "isCalendarDate",
  ({ value }) =>
    readDate(value).isValid() ? passes : fails("not-a-calendar-date"),
);

/**
 * A date no later than the day the context's `now` falls on in the UK.
 *
 * Cannot judge a value it cannot read as a date. Beside `isCalendarDate`,
 * which refuses that value itself, the two compose without one repeating the
 * other.
 */
export const notAfterToday = defineCheck<string>(
  "notAfterToday",
  ({ value, context }) => {
    if (!readDate(value).isValid()) {
      return cannotJudge(`"${value}" is not a calendar date`);
    }

    const today = calendarDayOf(context.now);

    if (today === undefined) {
      return cannotJudge(`the context's now, "${context.now}", is not a time`);
    }

    // Both are CALENDAR_DAY strings, which sort as the days they name.
    return value > today ? fails("after-today", { today }) : passes;
  },
);
